import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A company's logo, over its initials.
 *
 * The backend scrapes logos off the issuer's own website and records the file
 * under basic_company_details.logo_url. That pipeline is unreliable in three
 * ways: some records name a file the server never saved (404), some files hold
 * the scraped HTML page rather than an image, and the static host is slow
 * enough that a request can sit pending for a minute. Only the first two raise
 * the img's error event, so the initials are painted underneath and the image
 * is revealed once it actually decodes -- a logo that fails or never arrives
 * leaves the initials showing rather than an empty box.
 *
 * The image is loaded eagerly: tables paginate at ten rows, so lazy loading
 * saves nothing and only delays the swap.
 */
const SIZES = {
  xs: { box: 'w-7 h-7', radius: 'rounded-md', text: 'text-[10px]', pad: 'p-0.5' },
  sm: { box: 'w-8 h-8', radius: 'rounded-lg', text: 'text-xs', pad: 'p-1' },
  md: { box: 'w-10 h-10', radius: 'rounded-xl', text: 'text-sm', pad: 'p-1' },
  lg: { box: 'w-12 h-12 sm:w-14 sm:h-14', radius: 'rounded-xl', text: 'text-base sm:text-lg', pad: 'p-1.5' },
  xl: { box: 'w-16 h-16 sm:w-20 sm:h-20', radius: 'rounded-2xl', text: 'text-xl sm:text-2xl', pad: 'p-2' },
};

const initialsOf = (company, max) =>
  String(company || '')
    .replace(/\(.*?\)|\bLtd\.?|\bLimited\b/gi, '')
    .split(/[\s.&-]+/)
    .filter(Boolean)
    .slice(0, max)
    .map((word) => word[0].toUpperCase())
    .join('');

export const CompanyLogo = ({
  company,
  logoUrl,
  size = 'md',
  maxInitials = 2,
  className = '',
  fallbackClassName = 'bg-teal-50 border border-teal-100 text-[#006666]',
}) => {
  const imageRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // An image served from cache can finish before React attaches onLoad, so the
  // element is also read directly; without this a cached logo stays hidden.
  const syncFromElement = useCallback(() => {
    const image = imageRef.current;
    setLoaded(Boolean(image && image.complete && image.naturalWidth > 0));
  }, []);

  // A row keeps its component instance when a table pages or filters, so this
  // follows the logo the component currently points at
  useEffect(syncFromElement, [syncFromElement, logoUrl]);

  const box = SIZES[size] || SIZES.md;

  return (
    <span
      className={`relative ${box.box} ${box.radius} flex items-center justify-center flex-shrink-0 overflow-hidden ${box.text} font-extrabold ${
        loaded ? 'border border-slate-200 bg-white' : fallbackClassName
      } ${className}`}
    >
      <span aria-hidden="true">{initialsOf(company, maxInitials) || 'IPO'}</span>
      {logoUrl && (
        <img
          ref={imageRef}
          src={logoUrl}
          alt=""
          onLoad={syncFromElement}
          onError={syncFromElement}
          className={`absolute inset-0 w-full h-full object-contain ${box.pad} bg-white transition-opacity ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </span>
  );
};

export default CompanyLogo;
